import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, ApiError } from './api';

interface User {
  id: string;
  email: string;
  role: string;
  organizationId: string;
  memberId?: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    logoUrl?: string | null;
  };
  member?: {
    id: string;
    firstName: string;
    lastName: string;
    photoUrl?: string | null;
  } | null;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, organizationSlug: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationSlug: string;
}

const AuthContext = createContext<AuthContextType | null>(null);

const ACCESS_TOKEN_KEY = 'openshul_access_token';
const REFRESH_TOKEN_KEY = 'openshul_refresh_token';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get<User>('/auth/me');
      if (response.success && response.data) {
        setUser(response.data);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // Try to refresh token
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          try {
            const refreshResponse = await api.post<{ accessToken: string }>('/auth/refresh', {
              refreshToken,
            });
            if (refreshResponse.success && refreshResponse.data) {
              localStorage.setItem(ACCESS_TOKEN_KEY, refreshResponse.data.accessToken);
              api.setAccessToken(refreshResponse.data.accessToken);
              // Retry fetching user
              const retryResponse = await api.get<User>('/auth/me');
              if (retryResponse.success && retryResponse.data) {
                setUser(retryResponse.data);
                return;
              }
            }
          } catch {
            // Refresh failed, clear tokens
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
            api.setAccessToken(null);
          }
        }
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      api.setAccessToken(token);
      fetchUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [fetchUser]);

  const login = async (email: string, password: string, organizationSlug: string) => {
    const response = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: User;
    }>('/auth/login', { email, password, organizationSlug });

    if (response.success && response.data) {
      localStorage.setItem(ACCESS_TOKEN_KEY, response.data.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      api.setAccessToken(response.data.accessToken);
      setUser(response.data.user);
    }
  };

  const register = async (data: RegisterData) => {
    await api.post('/auth/register', data);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch {
      // Ignore logout errors
    }
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    api.setAccessToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
